// MoodCheckPanel.jsx — private daily mood check.
// STYLE-ONLY change (dashboard_v7): the slider inside a DashboardPanel card
// was restyled into a compact inline emoji pill. All state, the /api/mood/today
// load and the /api/mood/submit call are preserved exactly.
import React, { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON = "#00FF99";

const moodLabel = (v) => {
  if (v <= 2) return { label: "Struggling", color: "#f87171", emoji: "😔" };
  if (v <= 4) return { label: "Low",         color: "#fb923c", emoji: "😐" };
  if (v <= 6) return { label: "Okay",        color: "#fde047", emoji: "🙂" };
  if (v <= 8) return { label: "Good",        color: "#86efac", emoji: "😊" };
  return         { label: "Great!",          color: NEON,      emoji: "🤩" };
};

// Representative score for each emoji face in the inline pill. Selecting a
// face sets `value` to its bucket; submit still POSTs the numeric score.
const FACES = [
  { emoji: "😔", score: 2 },
  { emoji: "😐", score: 4 },
  { emoji: "🙂", score: 6 },
  { emoji: "😊", score: 8 },
  { emoji: "🤩", score: 10 },
];

const PILL_CSS = `
.moodpill{background:#0d0d0d;border:1px solid rgba(255,255,255,.10);height:36px;border-radius:999px;display:flex;align-items:center;padding:0 6px;gap:1px}
.moodpill button.face{background:transparent;border:none;font-size:15px;cursor:pointer;border-radius:999px;width:29px;height:29px;display:grid;place-items:center;filter:grayscale(.6);transition:.15s;padding:0}
.moodpill button.face:hover{filter:none;transform:scale(1.15)}
.moodpill button.face.sel{filter:none;background:rgba(0,255,153,.12);box-shadow:inset 0 0 0 1px rgba(0,255,153,.30)}
.moodpill .save{background:#00FF99;color:#000;border:none;border-radius:999px;height:26px;padding:0 11px;font-size:10.5px;font-weight:600;cursor:pointer;margin-left:5px;font-family:'Inter',sans-serif}
.moodpill .save:disabled{opacity:.45;cursor:default}
.moodpill .logged{display:flex;align-items:center;gap:7px;padding:0 8px;font-size:11.5px;font-weight:600;white-space:nowrap}
.moodpill .logged .upd{background:transparent;border:none;color:rgba(255,255,255,.45);font-size:10px;text-decoration:underline;cursor:pointer;padding:0}
`;

export default function MoodCheckPanel() {
  const [todayMood, setTodayMood] = useState(null);
  const [value, setValue] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  useEffect(() => {
    fetch(`${API_BASE}/api/mood/today`, { headers: headers() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.score != null) {
          setTodayMood(d);
          setValue(d.score);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const submitMood = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/mood/submit`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ score: value }),
      });
      if (res.ok) {
        const d = await res.json();
        setTodayMood(d);
      }
    } catch (e) {
      console.error("Mood submit failed:", e);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedEmoji = moodLabel(value).emoji;

  return (
    <>
      <style>{PILL_CSS}</style>
      <div className="moodpill">
        {loading ? (
          <span style={{ padding: "0 8px", fontSize: 11.5, color: "rgba(255,255,255,.45)" }}>…</span>
        ) : todayMood ? (
          <div className="logged">
            <span style={{ fontSize: 15 }}>{moodLabel(todayMood.score).emoji}</span>
            <span style={{ color: moodLabel(todayMood.score).color }}>
              {moodLabel(todayMood.score).label}
            </span>
            <button className="upd" onClick={() => setTodayMood(null)}>Update</button>
          </div>
        ) : (
          <>
            {FACES.map((f) => (
              <button
                key={f.score}
                type="button"
                className={`face${selectedEmoji === f.emoji ? " sel" : ""}`}
                onClick={() => setValue(f.score)}
              >
                {f.emoji}
              </button>
            ))}
            <button className="save" onClick={submitMood} disabled={submitting}>
              {submitting ? "…" : "Save"}
            </button>
          </>
        )}
      </div>
    </>
  );
}
